import type { DcOrderingTemplate } from "./types";

/**
 * How a family orders their own death certificates, by ordering process.
 *
 * Six templates cover all 119 counties After serves: VitalChek accounts for 94
 * of them, and the remaining 25 collapse into five because all 16 Texas
 * counties share one state portal and all 6 Utah counties likewise. Only the
 * three California counties are genuinely one-offs.
 *
 * Keyed by process rather than by county deliberately — a county-keyed map
 * would be 119 near-identical bodies of copy that drift the moment one is
 * edited. after-api resolves a case's county to one of these keys; the copy
 * itself lives here, alongside the screen that renders it.
 *
 * Copy is condensed from the family-facing PDF guides (Aug 2026). The guides
 * remain the source of truth for `instructions`, which is intentionally absent
 * here: it renders in the emailed guide and the After App, never in the
 * arrangement flow, so shipping it in the bundle would send six documents to
 * every visitor to display none of them.
 *
 * Timelines are also absent, and deliberately so. They vary county by county
 * inside a single template — most VitalChek counties are 3-4 weeks, but San
 * Diego and Ventura are "several weeks" — so they can't be template-keyed.
 * They arrive per case on the config instead (ENG-1470).
 *
 * NOT YET REVIEWED BY CARE. This copy tells a grieving family how to obtain a
 * legal document; Mariah or Care should sign off before the flag is enabled.
 */
export const DC_TEMPLATE_KEYS = [
  "vitalchek",
  "tx_state_portal",
  "ut_state_portal",
  "ca_san_francisco",
  "ca_san_bernardino",
  "ca_riverside",
] as const;

export type DcTemplateKey = (typeof DC_TEMPLATE_KEYS)[number];

/**
 * Shown under "We order them for you" when the market hasn't been modelled yet
 * (ENG-1479). Our own process rather than anything the county guides state.
 */
export const AFTER_ORDERS_TIMELINE_FALLBACK =
  "Usually 3 to 4 weeks after permits";

/**
 * Exhaustive by construction: adding a key to DC_TEMPLATE_KEYS without adding
 * its copy here is a compile error rather than a family staring at a blank
 * option.
 */
export const DC_ORDERING_TEMPLATES: Record<DcTemplateKey, DcOrderingTemplate> =
  {
    vitalchek: {
      description:
        "You'll order online through VitalChek, the official ordering partner for most county vital records offices. You'll need a photo ID, your relationship to your loved one, and a card. Certificates are mailed by the government agency itself, and you can pay for trackable shipping at checkout.",
    },

    tx_state_portal: {
      description:
        "Texas takes orders through the state's online portal, or in person at a local vital records office. You'll need a photo ID, and to be immediate family or have a direct, tangible interest in the record, such as settling an estate.",
    },

    ut_state_portal: {
      description:
        "Utah takes orders through the state's official portal, or in person at a local health department. You'll need a Utah.gov account, a photo ID, and proof of your relationship. Third-party ordering sites aren't accepted and those requests won't be processed.",
    },

    ca_san_francisco: {
      description:
        "San Francisco takes orders online for in-person pickup at the County Clerk in City Hall, or by mail with a notarized sworn statement. Mail delivery is handled through VitalChek.",
    },

    ca_san_bernardino: {
      description:
        "San Bernardino takes orders online through its Vitals Access portal for in-person pickup, or by mail with a notarized application. Certified copies are $26 each plus a card processing fee.",
    },

    ca_riverside: {
      description:
        "Riverside County takes orders three ways through the Assessor-County Clerk-Recorder: online, in person, or by mail. Certified copies need identity verification, or a notarized sworn statement if that doesn't go through. Copies are $26 each.",
    },
  };

export function getDcOrderingTemplate(
  key: string | null | undefined,
): DcOrderingTemplate | null {
  // Own keys only: a key naming an inherited member — "constructor",
  // "toString" — would otherwise resolve to a function rather than to no
  // template, and the screen renders whatever this returns.
  if (
    !key ||
    !Object.prototype.hasOwnProperty.call(DC_ORDERING_TEMPLATES, key)
  ) {
    return null;
  }
  return DC_ORDERING_TEMPLATES[key as DcTemplateKey];
}
